'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useImperativeHandle, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import hash from 'hash-object';
import { createExpandColumn, createSelectionColumn } from '../data-table/factories';
import { DataTableBody, DataTableBodySkeleton, InfiniteScrollFooter, PaginationFooter, SelectionActionBar } from '../data-table';
import { FlexContainer, Skeleton, Table, TableCell, TableHead, TableHeader, TableRow } from '../components';
import { useActiveRow, useClientTableModel, useInfiniteScrollPagination, useQueryRefresh, useRowExpansion, useRowSelection, useSearchParamPagination, useSearchParamsQuery, useTableSorting } from '../hooks';
import { rowIdResetKey } from '../hooks/useRowExpansion';
import { toGetRowIdOption, withRowId } from '../hooks/rowIdentity';
import { buildSanitizedKey } from '../hooks/searchParamsUtils';
import { sanitizeSearchParams } from '../table-filters/typed-filter-config';
import { TableSection } from '../table/TableSection';
import type { TableSectionLayoutVariant, TableSectionRefreshConfig, TableSectionRefreshContext } from '../table/TableSection';
import type { QueryKey } from '@tanstack/react-query';
import type { Pagination, PaginationType, TableToolbarContext } from '../data-table/types';
import type { NullStateProps } from '../components/NullState';
import type { ActiveRowConfig, ActiveRowHandle, ClientDataConfig, ResolvedClientDataConfig, RowExpansionConfig, RowSelectionConfig, UseActiveRowResult, UseRowExpansionResult, UseClientTableModelResult, UseRowSelectionResult } from '../hooks';
import type { GetRowId } from '../hooks/rowIdentity';
import type { DataTableExpansion } from '../data-table';
import type { ColumnDef, RowSelectionState, SortingState, Table as TableInstance, Updater } from '@tanstack/react-table';
import type { FilterConfigMap, InferSearchParams, PaginationSearchParams } from '../table-filters/typed-filter-config';
function resolveTableQueryKey<THookResults extends Record<string, unknown>>({
  cacheTag,
  sanitizedKey,
  hookResults,
  buildQueryKey
}: {
  cacheTag: string | string[];
  sanitizedKey: string;
  hookResults?: THookResults;
  buildQueryKey?: (cacheTag: string | string[], searchKey: string, hookResults?: THookResults) => unknown[];
}): QueryKey {
  // Both paths use sanitizedKey so only declared filter/pagination params
  // affect cache identity. UI-state params (e.g. ConfigurableTabs deepLink)
  // are excluded for all tables, whether they use a custom key or the default.
  return buildQueryKey ? buildQueryKey(cacheTag, sanitizedKey, hookResults) : [cacheTag, sanitizedKey];
}

/**
 * Native multi-row selection for the table. When provided, a leading checkbox
 * column is prepended and an always-visible selection action bar renders above
 * the table.
 */
export interface TableRowSelectionConfig<TRow> extends RowSelectionConfig<TRow> {
  /**
   * Renders the action controls shown in the selection bar. Receives the
   * selected row objects and a helper to clear them. Actions should stay
   * mounted and disable themselves when nothing is selected (the bar itself is
   * always visible).
   */
  renderActions?: (context: {
    selectedRows: TRow[];
    clearSelection: () => void;
  }) => React.ReactNode;
}

/**
 * Configuration for creating a table-specific Suspense wrapper component.
 * This utility is specifically designed for tables that need progressive loading
 * (header and pagination visible immediately, data loads separately).
 */
interface TableSuspenseWrapperBaseConfig<TData, TRow, TParams extends Record<string, unknown> = Record<string, unknown>, TFilterConfig extends FilterConfigMap = FilterConfigMap, TSortBy extends string = string, TColumnId extends string = never, THookResults extends Record<string, unknown> = Record<string, unknown>> {
  /** Cache tag(s) for React Query - can be a single tag or array of tags */
  cacheTag: string | string[];

  /** Fetcher function that accepts query params and returns data */
  fetcher: (params: TParams | undefined) => Promise<TData>;

  /** Column definitions for the table - required to render header immediately */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TRow, any>[];

  /**
   * Extract the data array from the API response.
   * Defaults to: (response) => response.data
   */
  getDataFromResponse?: (response: TData) => TRow[];

  /**
   * Extract pagination info from the API response.
   * Defaults to: (response) => ({ cursors: response.cursors, totalCount: response.totalCount })
   */
  getPaginationFromResponse?: (response: TData) => Pagination;

  /** Whether to hide the table header */
  hideHeader?: boolean;

  /** Null state props for empty tables */
  nullStateProps?: NullStateProps;

  /** Handler for row clicks */
  onRowClick?: (row: TRow) => void;

  /** Function to generate href for each row (makes rows actual links) */
  rowHref?: (row: TRow) => string;

  /** Whether to apply inset padding to cells (defaults to true) */
  inset?: boolean;

  /** Test ID for the table body */
  testId?: string;

  /**
   * Sorting configuration for the table.
   *
   * `id` refers to the TanStack column ID, which defaults to the column's accessor
   * (= `keyof TRow`). For display/action/custom-with-accessorFn columns, the `id`
   * is an arbitrary string — declare those in `TColumnId` to allow them here.
   *
   * `initialSortState` seeds the sort either way. `sortKeyMap` is ignored under
   * `clientData`, where TanStack sorts the loaded rows in the browser and no
   * query carries a sort key.
   */
  sorting?: {
    initialSortState?: {
      id: (keyof TRow & string) | TColumnId;
      desc: boolean;
    }[];
    sortKeyMap?: Record<string, TSortBy>;
  };

  /** Whether to use search params from URL (defaults to true) */
  useSearchParams?: boolean;

  /**
   * Options for useSearchParamsQuery hook.
   * Defaults to { sortEntries: true } for consistent query key generation.
   */
  searchParamsOptions?: {
    sortEntries?: boolean;
  };

  /** Build custom query key. The searchKey contains only declared filter/pagination params (sanitized). */
  buildQueryKey?: (cacheTag: string | string[], searchKey: string, hookResults?: THookResults) => unknown[];

  /**
   * Extra hooks feeding the query key and params (e.g. useSession, useParams).
   * Runs above the `<Suspense>` boundary, so it must not suspend: a suspending
   * hook takes the header/footer skeleton down with it, not just the table body.
   */
  useAdditionalHooks?: () => THookResults;

  /** Typed filter config (from `as const satisfies FilterConfigMap`) */
  filterConfig: TFilterConfig;

  /**
   * Build query parameters from sanitized filter params and pagination params.
   * Filter params are typed and runtime-validated against filterConfig.
   * Pagination params are the well-known cursor params (nextCursor, prevCursor).
   */
  buildQueryParams: (filterParams: InferSearchParams<TFilterConfig>, paginationParams: PaginationSearchParams<TSortBy>, hookResults?: THookResults) => TParams;

  /** Custom label for the total count in pagination footer (e.g., 'customers' instead of 'items') */
  totalCountLabel?: string;

  /**
   * Type of pagination to use
   * - 'buttons': Traditional pagination with Previous/Next buttons (default)
   * - 'infinite': Infinite scroll that accumulates all pages in memory
   */
  paginationType?: PaginationType;

  /** Called after each successful fetch with the current page's rows. */
  onDataLoaded?: (rows: TRow[]) => void;

  /**
   * Sort and search the already-fetched rows in the browser: the same column
   * headers drive TanStack sorting, and the toolbar search box filters through
   * `globalFilterFn`.
   */
  clientData?: ClientDataConfig<TRow>;

  /**
   * Controls rendered above the table (and above the selection bar): search box,
   * expand/collapse all, export, anything driven off the live table instance.
   */
  toolbar?: (context: TableToolbarContext<TRow>) => React.ReactNode;

  /** Optional section shell — title, refresh, and layout around the table. */
  section?: TableSectionConfig;
}
interface TableRowIdentityConfig<TRow> {
  /**
   * Stable id per row, shared by row selection, row expansion and the active row.
   * Required as soon as one of them is enabled.
   */
  getRowId: GetRowId<TRow>;
}

/** Features whose state is keyed by row, and so require the table's `getRowId`. */
interface TableRowKeyedFeatures<TRow, TChildRow> {
  /** Enable native multi-row selection (checkbox column + action bar). */
  rowSelection?: TableRowSelectionConfig<TRow>;
  /**
   * Enable a single active row for master/detail views. Row clicks activate,
   * on top of running `onRowClick`. The resolved row reaches the call site
   * through the `onActiveRowChange` prop.
   */
  activeRow?: ActiveRowConfig;
  /** Enable row expansion (chevron column + expanded content or child rows). */
  rowExpansion?: RowExpansionConfig<TRow, TChildRow>;
}

/** Every row-keyed feature shares one row identity, so none of them can key its state differently. */
export type CreateTableSuspenseWrapperConfig<TData, TRow, TParams extends Record<string, unknown> = Record<string, unknown>, TFilterConfig extends FilterConfigMap = FilterConfigMap, TSortBy extends string = string, TColumnId extends string = never, THookResults extends Record<string, unknown> = Record<string, unknown>, TChildRow = never> = TableSuspenseWrapperBaseConfig<TData, TRow, TParams, TFilterConfig, TSortBy, TColumnId, THookResults> & ((TableRowIdentityConfig<TRow> & TableRowKeyedFeatures<TRow, TChildRow>) | ({
  getRowId?: never;
} & { [K in keyof TableRowKeyedFeatures<TRow, TChildRow>]?: never }));
export interface TableSectionConfig {
  title: React.ReactNode;
  /** When true (or when a config object is passed), render a refresh control wired to this table's query key. */
  refresh?: boolean | TableSectionRefreshConfig;
  /** Extra header controls; receives `{ refresh, isRefreshing }` for coordinating with refresh. */
  headerActions?: React.ReactNode | ((ctx: TableSectionRefreshContext) => React.ReactNode);
  layout?: TableSectionLayoutVariant;
  /** Forwarded to `CollapsibleCard` when `layout="collapsible"`. */
  collapsibleDefaultOpen?: boolean;
  /** Forwarded to `CollapsibleCard` when `layout="collapsible"`. */
  collapsibleHeaderVariant?: 'bar' | 'row';
}
export type { TableSectionLayoutVariant, TableSectionRefreshConfig, TableSectionRefreshContext };

/**
 * Props that can be passed to the wrapper component to override static configuration.
 * This allows dynamic handlers that depend on hooks to be passed at render time.
 */
interface TableSuspenseWrapperProps<TRow> {
  /** Handler for row clicks - overrides config.onRowClick */
  onRowClick?: (row: TRow) => void;

  /** Function to generate href for each row - overrides config.rowHref */
  rowHref?: (row: TRow) => string;

  /** Null state props - overrides config.nullStateProps */
  nullStateProps?: NullStateProps;

  /** Called after each successful fetch with the current page's rows - overrides config.onDataLoaded */
  onDataLoaded?: (rows: TRow[]) => void;

  /** Callback to apply conditional class names to rows */
  rowClassName?: (row: TRow) => string | undefined;
}

/**
 * The active row's render-time channel. Only a config carrying `activeRow` returns
 * a component accepting these, so neither can be passed to a table that would
 * silently drop it.
 */
interface TableActiveRowProps<TRow> {
  /**
   * Called with the active row whenever it changes, and with `null` when it is
   * cleared. This is how a call site gets the resolved row, since the table
   * itself is built at module scope.
   */
  onActiveRowChange?: (row: TRow | null) => void;

  /**
   * Handle for clearing the active row, for a detail view that closes on its own
   * (the module-scope counterpart to `setActiveRow` from `useDataTable`).
   */
  activeRowRef?: React.Ref<ActiveRowHandle>;
}

/** Rebuilt every render so the handle always closes over the current data's resolution. */
function useActiveRowHandle(ref, active) {
  const $ = _c(2);
  let t0;
  if ($[0] !== active) {
    t0 = () => ({
      clear: () => active.setActiveRow(null)
    });
    $[0] = active;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useImperativeHandle(ref, t0);
}

/**
 * Creates a table-specific Suspense wrapper with progressive loading.
 * The table header and pagination footer render immediately in disabled state,
 * while only the table body shows a loading skeleton.
 *
 * A config enabling `activeRow` widens the returned component's props with the
 * active row's render-time channel; every other config rejects them.
 */
export function createTableSuspenseWrapper<TData = unknown, TRow = unknown, TParams extends Record<string, unknown> = Record<string, unknown>, TFilterConfig extends FilterConfigMap = FilterConfigMap, TSortBy extends string = string, TColumnId extends string = never, THookResults extends Record<string, unknown> = Record<string, unknown>, TChildRow = never>(config: TableSuspenseWrapperBaseConfig<TData, TRow, TParams, TFilterConfig, TSortBy, TColumnId, THookResults> & TableRowIdentityConfig<TRow> & TableRowKeyedFeatures<TRow, TChildRow> & {
  activeRow: ActiveRowConfig;
}): React.FunctionComponent<TableSuspenseWrapperProps<TRow> & TableActiveRowProps<TRow>>;
export function createTableSuspenseWrapper<TData = unknown, TRow = unknown, TParams extends Record<string, unknown> = Record<string, unknown>, TFilterConfig extends FilterConfigMap = FilterConfigMap, TSortBy extends string = string, TColumnId extends string = never, THookResults extends Record<string, unknown> = Record<string, unknown>, TChildRow = never>(config: CreateTableSuspenseWrapperConfig<TData, TRow, TParams, TFilterConfig, TSortBy, TColumnId, THookResults, TChildRow> & {
  activeRow?: never;
}): React.FunctionComponent<TableSuspenseWrapperProps<TRow>>;
export function createTableSuspenseWrapper<TData = unknown, TRow = unknown, TParams extends Record<string, unknown> = Record<string, unknown>, TFilterConfig extends FilterConfigMap = FilterConfigMap, TSortBy extends string = string, TColumnId extends string = never, THookResults extends Record<string, unknown> = Record<string, unknown>, TChildRow = never>(config: CreateTableSuspenseWrapperConfig<TData, TRow, TParams, TFilterConfig, TSortBy, TColumnId, THookResults, TChildRow>) {
  const {
    cacheTag,
    fetcher,
    columns,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDataFromResponse = (response: TData) => (response as any).data as TRow[],
    getPaginationFromResponse = (response: TData) => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cursors: (response as any).cursors,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      totalCount: (response as any).totalCount
    }),
    hideHeader = false,
    nullStateProps: configNullStateProps,
    onRowClick: configOnRowClick,
    rowHref: configRowHref,
    inset = true,
    testId,
    sorting: sortingConfig,
    useSearchParams = true,
    searchParamsOptions,
    buildQueryKey,
    useAdditionalHooks,
    filterConfig,
    buildQueryParams,
    totalCountLabel,
    paginationType = 'buttons',
    onDataLoaded,
    getRowId,
    rowSelection,
    activeRow,
    rowExpansion,
    clientData,
    toolbar,
    section
  } = config;

  // The one row-identity option, shared by the body table, the infinite-scroll
  // table and the header table, so every row-keyed feature and both table
  // instances agree on what identifies a row.
  const rowIdOption = getRowId ? toGetRowIdOption(getRowId) : undefined;
  const expansionConfig = withRowId(rowExpansion, getRowId);

  // Prepend the feature columns. Shared by the body table, the infinite-scroll
  // renderer, and the header table so the select-all header checkbox, the
  // per-row checkboxes, and the header/body column counts stay in sync.
  const tableColumns = rowSelection || rowExpansion ? [...(rowSelection ? [createSelectionColumn<TRow>({
    getDisabledReason: rowSelection.rowSelectionDisabledReason
  })] : []), ...(rowExpansion ? [createExpandColumn<TRow>()] : []), ...columns] : columns;
  const clientDataConfig: ResolvedClientDataConfig<TRow> | undefined = clientData ? {
    ...clientData,
    ...(sortingConfig?.initialSortState && {
      initialSorting: sortingConfig.initialSortState
    })
  } : undefined;

  /** Query inputs resolved above the Suspense boundary and handed to a container. */
  interface ResolvedTableQuery {
    hookResults?: THookResults;
    sanitizedKey: string;
    filterParams: InferSearchParams<TFilterConfig>;
    paginationParams: PaginationSearchParams<TSortBy>;
    queryKey: QueryKey;
  }
  type ContainerProps = TableSuspenseWrapperProps<TRow> & TableActiveRowProps<TRow> & {
    query: ResolvedTableQuery;
  };

  // Runs in `Wrapper`, above the boundary, so the section header can wire
  // refresh to the real query key on first paint.
  function useResolvedTableQuery(): ResolvedTableQuery {
    const hookResults = useAdditionalHooks?.();

    // Default to sortEntries: true for consistent query key generation.
    const searchParamsQuery = useSearchParamsQuery(searchParamsOptions ?? {
      sortEntries: true
    });
    const searchParamsObject = useSearchParams ? searchParamsQuery.searchParamsObject as Record<string, unknown> | undefined : undefined;
    const {
      filterParams,
      paginationParams
    } = sanitizeSearchParams(filterConfig, searchParamsObject, sortingConfig?.sortKeyMap ? {
      sortKeyMap: sortingConfig.sortKeyMap
    } : undefined);
    const sanitizedKey = buildSanitizedKey(filterParams, paginationParams);
    const queryKey = resolveTableQueryKey({
      cacheTag,
      sanitizedKey,
      hookResults,
      buildQueryKey
    });
    return {
      hookResults,
      sanitizedKey,
      filterParams,
      paginationParams,
      queryKey
    };
  }

  // Button pagination container
  function ButtonPaginationContainer({
    onRowClick: propsOnRowClick,
    rowHref: propsRowHref,
    nullStateProps: propsNullStateProps,
    onDataLoaded: propsOnDataLoaded,
    onActiveRowChange,
    activeRowRef,
    rowClassName: propsRowClassName,
    query
  }: ContainerProps) {
    // Props override config
    const onRowClick = propsOnRowClick ?? configOnRowClick;
    const rowHref = propsRowHref ?? configRowHref;
    const nullStateProps = propsNullStateProps ?? configNullStateProps;
    const onDataLoadedFn = propsOnDataLoaded ?? onDataLoaded;
    const rowClassName = propsRowClassName;
    const {
      hookResults,
      filterParams,
      paginationParams,
      queryKey
    } = query;
    const queryParams = buildQueryParams(filterParams, paginationParams, hookResults);

    // Fetch data with useSuspenseQuery
    const {
      data: response
    } = useSuspenseQuery({
      queryKey,
      queryFn: () => fetcher(queryParams)
    });

    // A transform that builds view rows returns fresh objects on every call, so the
    // rows are derived once per response: `onDataLoaded` and the active row's
    // `onChange` fire off this identity, and a call site feeding either into
    // `setState` would otherwise re-render, re-derive, and fire again forever.
    const data = useMemo(() => getDataFromResponse(response), [response]);
    const pagination = getPaginationFromResponse?.(response);

    // Generate the selection and expansion reset signal from the data content.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataKey = useMemo(() => hash(data as Record<string, any>), [data]);
    useEffect(() => {
      onDataLoadedFn?.(data);
    }, [data, onDataLoadedFn]);

    // Selection tracks the currently-visible page: `dataKey` changes when the
    // page or filter changes, resetting the selection so off-screen rows can't
    // stay selected.
    const selection = useRowSelection(rowSelection, dataKey);
    // Expansion resets on the row set, not on row content, so a refetch that only updates
    // fields on the rows already on screen leaves an open row open.
    const expansion = useRowExpansion(expansionConfig, expansionConfig ? rowIdResetKey(data, expansionConfig.getRowId) : dataKey);
    const client = useClientTableModel(clientDataConfig);

    // Setup pagination config - always call hook to follow Rules of Hooks
    // Use default empty pagination when not provided
    const paginationResult = useSearchParamPagination(pagination ?? {
      cursors: {}
    });

    // Only use pagination config if pagination was actually provided
    const paginationConfig = pagination ? paginationResult : undefined;

    // Create table instance with the loaded data
    const table = useReactTable({
      data,
      columns: tableColumns,
      state: {
        ...(selection.enabled && {
          rowSelection: selection.rowSelectionState
        }),
        ...(expansion.enabled && {
          expanded: expansion.expandedState
        }),
        ...(client.enabled && {
          sorting: client.sorting,
          globalFilter: client.globalFilter
        })
      },
      ...(rowIdOption && {
        getRowId: rowIdOption
      }),
      ...selection.tableOptions,
      ...expansion.tableOptions,
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      ...client.tableOptions,
      getCoreRowModel: getCoreRowModel()
    });

    // The rows actually on screen, which `clientData` sorting and searching reorder and
    // narrow. Every feature reading "the list" has to read this rather than the fetched
    // `data`, or it disagrees with what the user sees.
    const visibleRows = table.getRowModel().rows.map(row => row.original);
    const active = useActiveRow(resolveActiveRowConfig(onActiveRowChange), visibleRows);
    useActiveRowHandle(activeRowRef, active);
    const selectedRows = selection.getSelectedRows(table);
    const toggleRow = (row_0: TRow) => expansion.toggleRow(table, row_0);
    const shell = <TableShell clientSorting={client.enabled ? {
      sorting: client.sorting,
      setSorting: client.setSorting
    } : undefined} headerData={selection.enabled ? visibleRows : undefined} paginationFooter={paginationConfig ? <PaginationFooter {...paginationConfig} totalCountLabel={totalCountLabel} /> : null} rowSelectionState={selection.enabled ? selection.rowSelectionState : undefined} selectionOptions={selection.tableOptions} tableBody={<DataTableBody activeRowState={active.enabled ? active.tableState : undefined} expansion={resolveBodyExpansion(expansion, toggleRow)} headerGroups={table.getHeaderGroups()} inset={inset} nullStateProps={nullStateProps} onRowClick={onRowClick} rowClassName={rowClassName} rowHref={rowHref} rowModel={table.getRowModel()} rowSelectionState={selection.enabled ? selection.rowSelectionState : undefined} testId={testId} />} />;
    return renderChrome({
      shell,
      table,
      selectedRows,
      selection,
      toggleRow,
      client,
      queryKey
    });
  }

  // Infinite scroll container
  function InfiniteScrollContainer({
    onRowClick: propsOnRowClick,
    rowHref: propsRowHref,
    nullStateProps: propsNullStateProps,
    onDataLoaded: propsOnDataLoaded,
    onActiveRowChange,
    activeRowRef,
    rowClassName: propsRowClassName,
    query
  }: ContainerProps) {
    // Props override config
    const onRowClick = propsOnRowClick ?? configOnRowClick;
    const rowHref = propsRowHref ?? configRowHref;
    const nullStateProps = propsNullStateProps ?? configNullStateProps;
    const onDataLoadedFn = propsOnDataLoaded ?? onDataLoaded;
    const rowClassName = propsRowClassName;

    // `filterParams` and the sort stay stable across pages; the cursor is applied per
    // page below.
    const {
      hookResults,
      sanitizedKey,
      filterParams,
      paginationParams,
      queryKey
    } = query;

    // Fetch data with useInfiniteScrollPagination
    const {
      data,
      totalCount,
      hasNextPage,
      sentinelRef,
      isLoading
    } = useInfiniteScrollPagination<TData, TRow, HTMLTableRowElement, TParams>({
      queryKey,
      fetcher,
      getPageParams: cursor => buildQueryParams(filterParams, {
        ...paginationParams,
        nextCursor: cursor,
        prevCursor: undefined
      }, hookResults),
      getDataFromResponse,
      getPaginationFromResponse
    });
    useEffect(() => {
      onDataLoadedFn?.(data);
    }, [data, onDataLoadedFn]);

    // A filter/sort change replaces the accumulated list (`sanitizedKey` changes), so
    // drop the selection and the expansion too. Both intentionally persist across pages
    // appended under the same filter, since all appended rows stay visible; that is
    // why neither resets on the row-id set the way button pagination does.
    const selection = useRowSelection(rowSelection, sanitizedKey);
    const expansion = useRowExpansion(expansionConfig, sanitizedKey);
    const client = useClientTableModel(clientDataConfig);

    // Create table instance with the loaded data (must be called unconditionally for hooks rules)
    const table = useReactTable({
      data,
      columns: tableColumns,
      state: {
        ...(selection.enabled && {
          rowSelection: selection.rowSelectionState
        }),
        ...(expansion.enabled && {
          expanded: expansion.expandedState
        }),
        ...(client.enabled && {
          sorting: client.sorting,
          globalFilter: client.globalFilter
        })
      },
      ...(rowIdOption && {
        getRowId: rowIdOption
      }),
      ...selection.tableOptions,
      ...expansion.tableOptions,
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      ...client.tableOptions,
      getCoreRowModel: getCoreRowModel()
    });

    // See the note in the button-pagination container: features reading "the list" read
    // the rows on screen, not the accumulated `data`.
    const visibleRows = table.getRowModel().rows.map(row => row.original);
    const active = useActiveRow(resolveActiveRowConfig(onActiveRowChange), visibleRows);
    useActiveRowHandle(activeRowRef, active);
    const selectedRows = selection.getSelectedRows(table);
    const toggleRow = (row_0: TRow) => expansion.toggleRow(table, row_0);

    // Show skeleton during initial load (after all hooks are called)
    if (isLoading) {
      return <TableShell paginationFooter={<InfiniteScrollFooter totalCountLabel={totalCountLabel} />} tableBody={<DataTableBodySkeleton columns={tableColumns} inset={inset} />} />;
    }
    const shell = <TableShell clientSorting={client.enabled ? {
      sorting: client.sorting,
      setSorting: client.setSorting
    } : undefined} headerData={selection.enabled ? visibleRows : undefined} paginationFooter={<InfiniteScrollFooter totalCount={totalCount} totalCountLabel={totalCountLabel} />} rowSelectionState={selection.enabled ? selection.rowSelectionState : undefined} selectionOptions={selection.tableOptions} tableBody={<DataTableBody activeRowState={active.enabled ? active.tableState : undefined} expansion={resolveBodyExpansion(expansion, toggleRow)} headerGroups={table.getHeaderGroups()} inset={inset} nullStateProps={nullStateProps} onRowClick={onRowClick} rowClassName={rowClassName} rowHref={rowHref} rowModel={table.getRowModel()} rowSelectionState={selection.enabled ? selection.rowSelectionState : undefined} testId={testId} trailingRows={/* Skeleton rows for the next page; the first one is the intersection-observer sentinel. */
    hasNextPage && Array.from({
      length: 3
    }).map((_, index) => <TableRow key={`skeleton-${index}`} ref={index === 0 ? sentinelRef : undefined}>
                  {tableColumns.map((column, colIndex) => {
        const columnStyle = column.meta?.style;
        const textAlign = columnStyle?.textAlign;
        const alignmentClass = textAlign === 'right' ? 'ml-auto' : textAlign === 'center' ? 'mx-auto' : '';
        return <TableCell inset={inset} key={colIndex} style={columnStyle}>
                        <Skeleton className={alignmentClass} width="75%" />
                      </TableCell>;
      })}
                </TableRow>)} />} />;
    return renderChrome({
      shell,
      table,
      selectedRows,
      selection,
      toggleRow,
      client,
      queryKey
    });
  }

  // Main container that routes to the appropriate pagination type
  function Container(props) {
    const $ = _c(4);
    if (paginationType === "infinite") {
      let t0;
      if ($[0] !== props) {
        t0 = <InfiniteScrollContainer {...props} />;
        $[0] = props;
        $[1] = t0;
      } else {
        t0 = $[1];
      }
      return t0;
    }
    let t0;
    if ($[2] !== props) {
      t0 = <ButtonPaginationContainer {...props} />;
      $[2] = props;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  }

  /**
   * Row identity and the render-time `onActiveRowChange` prop are what the hook
   * needs on top of the static config.
   */
  function resolveActiveRowConfig(onActiveRowChange?: (row: TRow | null) => void) {
    return withRowId(activeRow ? {
      ...activeRow,
      onChange: onActiveRowChange
    } : undefined, getRowId);
  }
  function resolveBodyExpansion(expansion: UseRowExpansionResult<TRow>, toggleRow: (row: TRow) => void): DataTableExpansion<TRow, TChildRow> | undefined {
    if (!rowExpansion) return undefined;
    return {
      expandedState: expansion.expandedState,
      renderExpandedContent: rowExpansion.renderExpandedContent,
      getSubRows: rowExpansion.getSubRows,
      childColumns: rowExpansion.childColumns,
      onChildRowClick: rowExpansion.onChildRowClick,
      childRowHref: rowExpansion.childRowHref,
      ...(rowExpansion.expandOnRowClick && {
        toggleOnRowClick: toggleRow
      })
    };
  }

  /** Toolbar lives in its own component so only toolbar tables subscribe to fetch state. */
  function Toolbar(t0) {
    const $ = _c(7);
    const {
      table,
      toggleRow,
      client,
      queryKey
    } = t0;
    const {
      isRefreshing
    } = useQueryRefresh(queryKey);
    let t1;
    if ($[0] !== client || $[1] !== isRefreshing || $[2] !== table || $[3] !== toggleRow) {
      t1 = toolbar?.({
        table,
        globalFilter: client.globalFilter,
        onGlobalFilterChange: client.setGlobalFilter,
        isFetching: isRefreshing,
        toggleRow
      });
      $[0] = client;
      $[1] = isRefreshing;
      $[2] = table;
      $[3] = toggleRow;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t1) {
      t2 = <>{t1}</>;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }

  // Wraps a rendered table shell with the toolbar and the always-visible selection
  // action bar. Shared by both pagination containers so neither can diverge.
  function renderChrome({
    shell,
    table,
    selectedRows,
    selection,
    toggleRow,
    client,
    queryKey
  }: {
    shell: React.ReactNode;
    table: TableInstance<TRow>;
    selectedRows: TRow[];
    selection: UseRowSelectionResult<TRow>;
    toggleRow: (row: TRow) => void;
    client: UseClientTableModelResult<TRow>;
    queryKey: QueryKey;
  }): React.ReactNode {
    if (!rowSelection && !toolbar) return shell;
    return <FlexContainer className="min-h-0 flex-1 gap-3" direction="column" items="stretch">
        {toolbar && <Toolbar client={client} queryKey={queryKey} table={table} toggleRow={toggleRow} />}
        {rowSelection && <SelectionActionBar onClear={selection.clearSelection} selectedCount={selectedRows.length}>
            {rowSelection.renderActions?.({
          selectedRows,
          clearSelection: selection.clearSelection
        })}
          </SelectionActionBar>}
        {shell}
      </FlexContainer>;
  }
  function TableShell({
    tableBody,
    paginationFooter,
    headerData,
    rowSelectionState,
    selectionOptions,
    clientSorting
  }: {
    tableBody: React.ReactNode;
    paginationFooter: React.ReactNode;
    /** Real page rows, so the header select-all checkbox reflects body selection. */
    headerData?: TRow[];
    rowSelectionState?: RowSelectionState;
    /** Selection options from `useRowSelection` — shared with the body table so both stay in sync. */
    selectionOptions?: UseRowSelectionResult<TRow>['tableOptions'];
    /**
     * Client-mode sort state from the body table's model. Wiring it here is what
     * lets a click on these headers drive TanStack sorting.
     */
    clientSorting?: {
      sorting: SortingState;
      setSorting: (updater: Updater<SortingState>) => void;
    };
  }) {
    // Always call useTableSorting to follow Rules of Hooks (hooks must be called unconditionally)
    // Pass a default config when sortingConfig is not provided
    const urlSorting = useTableSorting(sortingConfig ?? {});
    const sorting = clientSorting ?? (sortingConfig ? urlSorting : undefined);

    // Create a table instance with a single dummy row to help calculate proper column widths
    // This ensures the header columns match the width they'll have when data loads
    const dummyRow = useMemo(() => {
      const row: Record<string, unknown> = {};
      tableColumns.forEach(col => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const accessor = (col as any).accessorKey || (col as any).id;
        if (accessor) {
          row[accessor] = '';
        }
      });
      return row as TRow;
    }, []);

    // The header shares the body's selection state (both tables are controlled by
    // the same options/state) so the select-all checkbox reflects and drives row
    // selection. Only wired once real data is present — the Suspense fallback
    // keeps the dummy row and leaves the header select-all disabled.
    const headerSelectionEnabled = !!headerData && !!selectionOptions?.onRowSelectionChange;
    const headerTable = useReactTable({
      data: headerData ?? [dummyRow],
      columns: tableColumns,
      state: {
        ...(sorting && {
          sorting: sorting.sorting
        }),
        ...(headerSelectionEnabled && {
          rowSelection: rowSelectionState ?? {}
        })
      },
      ...(sorting && {
        onSortingChange: sorting.setSorting
      }),
      // Same row identity as the body table, so the select-all header reads the state the
      // row checkboxes write. Left off the dummy sizing row, which no id function expects.
      ...(headerData && rowIdOption && {
        getRowId: rowIdOption
      }),
      // When the header isn't wired to real data (Suspense fallback), keep the dummy
      // sizing row non-selectable so the select-all checkbox disables itself.
      ...(headerSelectionEnabled ? selectionOptions : {
        enableRowSelection: false
      }),
      manualSorting: true,
      getCoreRowModel: getCoreRowModel()
    });
    const headerGroups = headerTable.getHeaderGroups();
    return <div className="flex min-h-0 w-full flex-1 flex-col">
        <Table>
          <TableHeader className={hideHeader ? 'invisible h-0! leading-0 [&_th]:h-0! [&_th]:p-0!' : ''}>
            {headerGroups.map(headerGroup => <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
              const headerWidth = header.getSize();
              const hasExplicitWidth = headerWidth !== 150; // 150 is TanStack Table's default
              const sortEntry = sorting?.sorting.find(({
                id
              }) => id === header.column.id);
              const ariaSort = sortEntry && (sortEntry.desc ? 'descending' : 'ascending');
              return <TableHead className="group" aria-sort={ariaSort} key={header.id} style={{
                // Set explicit widths to prevent column shifts during loading
                ...(hasExplicitWidth && {
                  width: headerWidth,
                  minWidth: headerWidth
                }),
                ...((header.column.columnDef.meta as {
                  style?: React.CSSProperties;
                })?.style || {})
              }}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>;
            })}
              </TableRow>)}
          </TableHeader>
          {tableBody}
        </Table>
        {paginationFooter}
      </div>;
  }
  function Wrapper(props) {
    const $ = _c(7);
    const query = useResolvedTableQuery();
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <TableShell paginationFooter={<PaginationFooter disabled={true} totalCountLabel={totalCountLabel} />} tableBody={<DataTableBodySkeleton columns={tableColumns} inset={inset} />} />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    const suspenseFallback = t0;
    let t1;
    if ($[1] !== props || $[2] !== query) {
      t1 = <Suspense fallback={suspenseFallback}><Container {...props} query={query} /></Suspense>;
      $[1] = props;
      $[2] = query;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    const table = t1;
    if (!section) {
      return table;
    }
    let t2;
    if ($[4] !== query.queryKey || $[5] !== table) {
      t2 = <TableSection collapsibleDefaultOpen={section.collapsibleDefaultOpen} collapsibleHeaderVariant={section.collapsibleHeaderVariant} headerActions={section.headerActions} layout={section.layout} queryKey={query.queryKey} refresh={section.refresh ?? false} title={section.title}>{table}</TableSection>;
      $[4] = query.queryKey;
      $[5] = table;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }

  // Set display names for debugging
  Wrapper.displayName = `TableSuspenseWrapper`;
  Container.displayName = `TableSuspenseContainer`;
  TableShell.displayName = `TableShell`;
  Toolbar.displayName = `TableToolbar`;
  return Wrapper;
}